/* ==========================================================================
   <supplement-quiz>: step-by-step questions → best matching result.
   Scoring: each result lists "key=value" rules; the result matching the most
   rules wins (ties: first in order). Budget answers like "0-100" filter the
   shown products by price. Answers are remembered for the session.
   ========================================================================== */
if (!customElements.get('supplement-quiz')) {
  const STORE = 'xh-quiz-answers';

  customElements.define(
    'supplement-quiz',
    class SupplementQuiz extends HTMLElement {
      connectedCallback() {
        this.steps = [...this.querySelectorAll('[data-step]')];
        this.screens = Object.fromEntries([...this.querySelectorAll('[data-screen]')].map((el) => [el.dataset.screen, el]));
        this.nextButton = this.querySelector('[data-quiz-next]');
        this.backButton = this.querySelector('[data-quiz-back]');
        this.index = 0;

        this.querySelector('[data-quiz-start]')?.addEventListener('click', () => this.start());
        this.nextButton?.addEventListener('click', () => this.next());
        this.backButton?.addEventListener('click', () => this.back());
        this.querySelector('[data-quiz-restart]')?.addEventListener('click', () => this.restart());
        this.addEventListener('change', (event) => {
          if (!event.target.matches('.quiz__input')) return;
          this.updateNext();
          // Single-choice questions advance automatically
          const step = this.steps[this.index];
          if (!step.hasAttribute('data-multiple') && !window.theme.utils.prefersReducedMotion()) {
            clearTimeout(this.autoTimer);
            this.autoTimer = setTimeout(() => this.next(), 350);
          }
        });
      }

      show(name) {
        Object.entries(this.screens).forEach(([key, el]) => (el.hidden = key !== name));
        this.scrollIntoView({ behavior: window.theme.utils.prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
      }

      start() {
        this.index = 0;
        this.show('steps');
        this.renderStep();
      }

      restart() {
        this.querySelectorAll('.quiz__input').forEach((input) => (input.checked = false));
        try {
          sessionStorage.removeItem(STORE);
        } catch {
          /* ignore */
        }
        this.start();
      }

      renderStep() {
        this.steps.forEach((step, i) => (step.hidden = i !== this.index));
        const total = this.steps.length;
        const label = window.theme.strings.quizStep.replace('[current]', this.index + 1).replace('[total]', total);
        this.querySelector('[data-step-label]').textContent = label;
        this.querySelector('[data-progress]').style.inlineSize = `${((this.index + 1) / total) * 100}%`;
        this.backButton.hidden = this.index === 0;
        this.querySelector('[data-next-label]').textContent =
          this.index === total - 1 ? window.theme.strings.quizResult : window.theme.strings.quizNext;
        this.updateNext();
        const first = this.steps[this.index]?.querySelector('.quiz__input:checked, .quiz__input');
        first?.focus({ preventScroll: true });
      }

      updateNext() {
        const step = this.steps[this.index];
        this.nextButton.disabled = !step?.querySelector('.quiz__input:checked');
      }

      next() {
        clearTimeout(this.autoTimer);
        if (this.nextButton.disabled) return;
        if (this.index < this.steps.length - 1) {
          this.index += 1;
          this.renderStep();
        } else {
          this.finish();
        }
      }

      back() {
        clearTimeout(this.autoTimer);
        if (this.index > 0) {
          this.index -= 1;
          this.renderStep();
        }
      }

      answers() {
        const answers = {};
        this.steps.forEach((step) => {
          const key = step.dataset.key;
          const checked = [...step.querySelectorAll('.quiz__input:checked')];
          answers[key] = { values: checked.map((i) => i.value), labels: checked.map((i) => i.dataset.label) };
        });
        return answers;
      }

      finish() {
        const answers = this.answers();
        try {
          sessionStorage.setItem(STORE, JSON.stringify(answers));
        } catch {
          /* ignore */
        }

        // Summary chips
        const summary = this.querySelector('[data-summary]');
        summary.replaceChildren(
          ...Object.values(answers)
            .flatMap((a) => a.labels)
            .map((label) => {
              const li = document.createElement('li');
              li.className = 'search__chip';
              li.textContent = label;
              return li;
            })
        );

        // Score results
        const results = [...this.querySelectorAll('[data-result]')];
        let best = null;
        let bestScore = 0;
        results.forEach((result) => {
          result.hidden = true;
          const rules = (result.dataset.rules || '').split(';').map((r) => r.trim()).filter(Boolean);
          const score = rules.reduce((sum, rule) => {
            const [key, value] = rule.split('=').map((p) => p.trim());
            return sum + (answers[key]?.values.includes(value) ? 1 : 0);
          }, 0);
          if (score > bestScore) {
            best = result;
            bestScore = score;
          }
        });

        this.querySelector('[data-fallback]').hidden = Boolean(best);
        if (best) {
          best.hidden = false;
          this.applyBudget(best, answers.budget?.values[0]);
        }
        this.show('results');
        this.screens.results.focus({ preventScroll: true });
      }

      applyBudget(result, budget) {
        const items = [...result.querySelectorAll('[data-price]')];
        const match = /^(\d+)\s*-\s*(\d+)?$/.exec(budget || '');
        let visible = 0;
        items.forEach((item) => {
          if (!match) {
            item.hidden = false;
            visible += 1;
            return;
          }
          const price = Number(item.dataset.price) / 100;
          const min = Number(match[1]);
          const max = match[2] ? Number(match[2]) : Infinity;
          item.hidden = price < min || price > max;
          if (!item.hidden) visible += 1;
        });
        // Never show an empty result: fall back to all products with a note
        if (visible === 0 && items.length) {
          items.forEach((item) => (item.hidden = false));
          const note = result.querySelector('[data-no-budget]');
          if (note) note.hidden = false;
        }
      }
    }
  );
}
